import * as React from 'react';
import {
  Container, Box, Paper, TextField, InputAdornment, Button,
  Stack, Chip, IconButton, Drawer, Select, MenuItem, FormControl,
  InputLabel, CircularProgress, Typography, Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { ordenesProveedor } from '../Componentes/FunctionLogin.js';
import MetricsDashboard from './MetricsDashboard';
import OrderCard from './OrderCard';
import EmptyState from './EmptyState';

const XLSX = require('xlsx');

export default function OrdersList() {
  // State
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const ordersPerPage = 10;

  // Filters
  let date = new Date();
  date.setMonth(date.getMonth() - 1);
  const [startDate, setStartDate] = React.useState(dayjs(date.toISOString().split('T')[0]));
  const [endDate, setEndDate] = React.useState(dayjs(new Date().toISOString().split('T')[0]));
  const [statusFilter, setStatusFilter] = React.useState(' ');
  const [locationFilter, setLocationFilter] = React.useState(' ');

  // Active filters for chips
  const [activeFilters, setActiveFilters] = React.useState([]);

  // Load orders on mount and when filters change
  React.useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const result = await ordenesProveedor(
        new Date(startDate.$d).toISOString().split('T')[0],
        new Date(endDate.$d).toISOString().split('T')[0],
        statusFilter,
        locationFilter
      );
      setOrders(result);
      updateActiveFilters();
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateActiveFilters = () => {
    const filters = [];
    if (statusFilter !== ' ') {
      const statusLabels = {
        " and CITM_LFCYCLE_ST eq '10' ": 'Factura Contabilizada',
        " and CITM_LFCYCLE_ST eq '6' ": 'Pendiente de entrega',
        " and CITM_LFCYCLE_ST eq '8' ": 'Cancelado',
        " and CITM_LFCYCLE_ST eq '9' ": 'Entregado'
      };
      filters.push({ type: 'status', label: statusLabels[statusFilter] || 'Estado' });
    }
    if (locationFilter !== ' ') {
      const locationLabels = {
        "and ((CRECEIVING_SITE eq 'CC1101')  or  (CRECEIVING_SITE eq 'CC1103'))": 'Itagüí',
        "and ((CRECEIVING_SITE eq 'CC1201')  or  (CRECEIVING_SITE eq 'CC1203'))": 'Bogotá',
        "and ((CRECEIVING_SITE eq 'CC1301')  or  (CRECEIVING_SITE eq 'CC1303'))": 'Cali',
        "and ((CRECEIVING_SITE eq 'CC1401')  or  (CRECEIVING_SITE eq 'CC1403'))": 'Barranquilla',
        "and ((CRECEIVING_SITE eq 'CC1501')  or  (CRECEIVING_SITE eq 'CC1503'))": 'Bucaramanga',
        "and ((CRECEIVING_SITE eq 'CC1701')  or  (CRECEIVING_SITE eq 'CC1703'))": 'Cúcuta'
      };
      filters.push({ type: 'location', label: locationLabels[locationFilter] || 'Sede' });
    }
    setActiveFilters(filters);
  };

  const handleRemoveFilter = (type) => {
    if (type === 'status') setStatusFilter(' ');
    if (type === 'location') setLocationFilter(' ');
    setTimeout(loadOrders, 100);
  };

  const handleClearAllFilters = () => {
    setStatusFilter(' ');
    setLocationFilter(' ');
    setSearchTerm('');
    setTimeout(loadOrders, 100);
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredOrders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Ordenes');
    XLSX.writeFile(workbook, `ordenes_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Filter orders by search term
  const filteredOrders = orders.filter(order => {
    const searchLower = searchTerm.toLowerCase();
    return (
      order.IdPedido.toLowerCase().includes(searchLower) ||
      order.ProductoId.toLowerCase().includes(searchLower) ||
      order.DescProducto.toLowerCase().includes(searchLower) ||
      order.NumeroPiezaProveedor.toLowerCase().includes(searchLower)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * ordersPerPage,
    page * ordersPerPage
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Search and Actions Bar */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Buscar por OC, producto, descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '250px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setFilterDrawerOpen(true)}
            sx={{ borderColor: '#BD1F34', color: '#BD1F34' }}
          >
            Filtros
          </Button>

          <IconButton
            color="primary"
            onClick={loadOrders}
            sx={{ backgroundColor: '#F3F4F6' }}
          >
            <RefreshIcon />
          </IconButton>

          <IconButton
            color="primary"
            onClick={handleExportExcel}
            disabled={filteredOrders.length === 0}
            sx={{ backgroundColor: '#F3F4F6' }}
          >
            <FileDownloadIcon />
          </IconButton>
        </Stack>

        {/* Active Filters Chips */}
        {activeFilters.length > 0 && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 600 }}>
              Filtros activos:
            </Typography>
            {activeFilters.map((filter, index) => (
              <Chip
                key={index}
                label={filter.label}
                onDelete={() => handleRemoveFilter(filter.type)}
                color="primary"
                size="small"
              />
            ))}
            <Button
              size="small"
              onClick={handleClearAllFilters}
              sx={{ ml: 1, textTransform: 'none' }}
            >
              Limpiar todos
            </Button>
          </Box>
        )}
      </Paper>

      {/* Metrics Dashboard */}
      <MetricsDashboard orders={orders} />

      {/* Orders List */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} sx={{ color: '#BD1F34' }} />
        </Box>
      ) : paginatedOrders.length > 0 ? (
        <Box>
          <Typography variant="body2" sx={{ mb: 2, color: '#6B7280' }}>
            Mostrando {paginatedOrders.length} de {filteredOrders.length} órdenes
          </Typography>

          <Stack spacing={2}>
            {paginatedOrders.map((order) => (
              <OrderCard key={order.IdPedido + order.ProductoId + Math.random()} order={order} />
            ))}
          </Stack>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </Box>
      ) : (
        <EmptyState
          type={searchTerm || activeFilters.length > 0 ? 'no-results' : 'no-data'}
          onClear={handleClearAllFilters}
        />
      )}

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
      >
        <Box sx={{ width: 350, p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Filtros
            </Typography>
            <IconButton onClick={() => setFilterDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Stack spacing={3}>
            {/* Date Range */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Rango de Fechas
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack spacing={2}>
                  <DatePicker
                    label="Fecha inicial"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                  <DatePicker
                    label="Fecha final"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Stack>
              </LocalizationProvider>
            </Box>

            {/* Status Filter */}
            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                value={statusFilter}
                label="Estado"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value=" ">Todos los estados</MenuItem>
                <MenuItem value=" and CITM_LFCYCLE_ST eq '10' ">Factura Contabilizada</MenuItem>
                <MenuItem value=" and CITM_LFCYCLE_ST eq '6' ">Pendiente de entrega</MenuItem>
                <MenuItem value=" and CITM_LFCYCLE_ST eq '8' ">Cancelado</MenuItem>
                <MenuItem value=" and CITM_LFCYCLE_ST eq '9' ">Entregado</MenuItem>
              </Select>
            </FormControl>

            {/* Location Filter */}
            <FormControl fullWidth>
              <InputLabel>Sede</InputLabel>
              <Select
                value={locationFilter}
                label="Sede"
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <MenuItem value=" ">Todas las Sedes</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1101')  or  (CRECEIVING_SITE eq 'CC1103'))">Itagüí</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1201')  or  (CRECEIVING_SITE eq 'CC1203'))">Bogotá</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1301')  or  (CRECEIVING_SITE eq 'CC1303'))">Cali</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1401')  or  (CRECEIVING_SITE eq 'CC1403'))">Barranquilla</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1501')  or  (CRECEIVING_SITE eq 'CC1503'))">Bucaramanga</MenuItem>
                <MenuItem value="and ((CRECEIVING_SITE eq 'CC1701')  or  (CRECEIVING_SITE eq 'CC1703'))">Cúcuta</MenuItem>
              </Select>
            </FormControl>

            {/* Apply Filters Button */}
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                loadOrders();
                setFilterDrawerOpen(false);
              }}
              sx={{
                mt: 2,
                backgroundColor: '#BD1F34',
                '&:hover': { backgroundColor: '#9a1829' }
              }}
            >
              Aplicar Filtros
            </Button>
          </Stack>
        </Box>
      </Drawer>
    </Container>
  );
}